'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFormsStore } from '@repo/store/useFormsStore'
import { useThirdPartyFormsStore } from '@repo/store/useThirdPartyFormsStore'
import { WizardFooter } from '@repo/ui/WizardFooter'
import { WizardPanel } from '@repo/ui/WizardPanel'
import MUICheckbox from '@mui/material/Checkbox'
import MUICircularProgress from '@mui/material/CircularProgress'
import MUIFormControlLabel from '@mui/material/FormControlLabel'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import { MapFieldsRow } from './MapFieldsRow'
import {
  FormField,
  fromMappedFormFieldExtended,
  MappedFormField,
  MappedFormFieldExtended,
  OspreyFieldSelectValue,
  toMappedFormField
} from './mapFieldsStepHelpers'

type StepProps = {
  wideMode?: boolean
  handlePrevNext: (delta: number) => void
  updateStepState: (changes: { status?: string; statusText?: string }) => void
  testMode?: boolean
}

type WorkflowType = { workflowTypeId: string; description: string }

type MappedFormData = {
  formId: string | null
  mappedFormId: string | null
  mappingType: 'global' | 'worktype'
  workTypes: WorkflowType[]
  fieldMappings: Record<string, OspreyFieldSelectValue | null>
}

export const MapFieldsStep = ({ wideMode, handlePrevNext, updateStepState }: StepProps) => {
  const navigate = useNavigate()

  const {
    mappedFormData,
    setMappedFormData,
    fetchFormFieldsByFormId,
    createMappedForm,
    fetchMappedFormFieldsByMappedFormId,
    createOrUpdateAllMappedFormFields
  } = useThirdPartyFormsStore() as {
    mappedFormData: MappedFormData
    setMappedFormData: (payload: { mappedFormData: Partial<MappedFormData> }) => void
    fetchFormFieldsByFormId: (args: { formId: string | null }) => Promise<FormField[]>
    createMappedForm: (args: { formId: string | null }) => Promise<string>
    fetchMappedFormFieldsByMappedFormId: (args: {
      mappedFormId: string | null
    }) => Promise<MappedFormFieldExtended[]>
    createOrUpdateAllMappedFormFields: (args: { mappedFormFields: MappedFormField[] }) => Promise<unknown>
  }

  const { getForm } = useFormsStore() as {
    getForm: (args: { formId: string | null }) => Promise<{ name?: string } | null>
  }

  const { formId, workTypes } = mappedFormData
  const workflowTypeId = workTypes?.[0]?.workflowTypeId

  const [formName, setFormName] = useState('')
  const [formFields, setFormFields] = useState<FormField[]>([])
  const [existingMappedFormFieldIds, setExistingMappedFormFieldIds] = useState<Record<string, string>>({})
  const [readOnlyFormulaFieldIds, setReadOnlyFormulaFieldIds] = useState<Set<string>>(new Set())
  const [showReadOnly, setShowReadOnly] = useState(true)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const isMountedRef = useRef(true)

  useEffect(() => {
    let cancelled = false
    isMountedRef.current = true

    const load = async () => {
      try {
        const [fields, form] = await Promise.all([fetchFormFieldsByFormId({ formId }), getForm({ formId })])
        if (cancelled) return

        setFormFields(Array.isArray(fields) ? fields : [])
        setFormName(form?.name || '')

        // Resolve a mappedFormId either way (createMappedForm is idempotent per OS-2722 --
        // it returns an existing mapping's id rather than creating a duplicate), then
        // always load whatever rows already exist for it. A brand-new, never-mapped form
        // simply has zero rows, so this is a no-op seed of {} for that case -- the only
        // case this changes is an already-mapped form, which now shows its real rows
        // instead of a blank screen (and no longer wipes them on Save).
        const resolvedMappedFormId = mappedFormData.mappedFormId ?? (await createMappedForm({ formId }))
        if (cancelled) return

        const existingRows = await fetchMappedFormFieldsByMappedFormId({ mappedFormId: resolvedMappedFormId })
        if (cancelled) return

        const nextFieldMappings: Record<string, OspreyFieldSelectValue | null> = {}
        const nextExistingIds: Record<string, string> = {}
        const nextReadOnlyFormulaIds = new Set<string>()

        ;(existingRows || []).forEach((row) => {
          if (!row?.formFieldId) return
          const value = fromMappedFormFieldExtended(row)
          if (value) nextFieldMappings[row.formFieldId] = value
          if (row.mappedFormFieldId) nextExistingIds[row.formFieldId] = row.mappedFormFieldId
          if (row.mappingType === 'Formula') nextReadOnlyFormulaIds.add(row.formFieldId)
        })

        setMappedFormData({ mappedFormData: { mappedFormId: resolvedMappedFormId, fieldMappings: nextFieldMappings } })
        setExistingMappedFormFieldIds(nextExistingIds)
        setReadOnlyFormulaFieldIds(nextReadOnlyFormulaIds)
      } catch (error) {
        // ospreyAPI's own error toast already surfaces this to the user; caught here only
        // so a failed initial load doesn't propagate as an unhandled rejection.
        console.error('Error loading Map fields step data:', error)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    load()
    return () => {
      cancelled = true
      isMountedRef.current = false
    }
    // Mount-only: this decides create-vs-load (AC-06/AC-07) from the mappedFormId this step
    // was entered with. Re-running it off a later mappedFormData change would repeat the
    // create call the first branch already made.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFieldChange = (formFieldId: string) => (value: OspreyFieldSelectValue | null) => {
    setMappedFormData({
      mappedFormData: { fieldMappings: { ...mappedFormData.fieldMappings, [formFieldId]: value } }
    })
  }

  const visibleFormFields = useMemo(
    () => (showReadOnly ? formFields : formFields.filter((field) => !field.isReadOnly && !field.isAutoCompleted)),
    [formFields, showReadOnly]
  )

  const handleSave = async () => {
    if (isSaving) return
    setIsSaving(true)

    try {
      const mappedFormFields = formFields
        .map((field) =>
          toMappedFormField({
            value: mappedFormData.fieldMappings?.[field.formFieldId],
            formFieldId: field.formFieldId,
            mappedFormId: mappedFormData.mappedFormId,
            mappedFormFieldId: existingMappedFormFieldIds[field.formFieldId] ?? null
          })
        )
        .filter(Boolean) as MappedFormField[]

      await createOrUpdateAllMappedFormFields({ mappedFormFields })
      if (!isMountedRef.current) return
      await updateStepState({ status: 'completed', statusText: 'Complete' })
      if (!isMountedRef.current) return
      navigate('?')
    } finally {
      if (isMountedRef.current) setIsSaving(false)
    }
  }

  return (
    <>
      <WizardPanel data-testid="MapFieldsStep">
        <MUIStack gap={2}>
          <MUIStack direction="row" gap={2}>
            <MUITypography sx={{ fontWeight: 700, flex: 1, mb: 0 }}>OSPREY</MUITypography>
            <MUITypography sx={{ fontWeight: 700, flex: 1, mb: 0 }}>{`FormEvo — ${formName}`}</MUITypography>
          </MUIStack>

          <MUIFormControlLabel
            control={
              <MUICheckbox
                checked={showReadOnly}
                onChange={(event) => setShowReadOnly(event.target.checked)}
                inputProps={{ 'aria-label': 'Show read-only fields' } as any}
              />
            }
            label="Show read-only fields"
          />

          {isLoading ? (
            <MUIStack alignItems="center" sx={{ py: 4 }}>
              <MUICircularProgress size={28} />
            </MUIStack>
          ) : (
            <MUIStack gap={2}>
              {visibleFormFields.map((field) => (
                <MapFieldsRow
                  key={field.formFieldId}
                  formField={field}
                  value={mappedFormData.fieldMappings?.[field.formFieldId] ?? null}
                  onChange={handleFieldChange(field.formFieldId)}
                  workflowTypeId={workflowTypeId}
                  isReadOnlyFormula={readOnlyFormulaFieldIds.has(field.formFieldId)}
                />
              ))}
            </MUIStack>
          )}
        </MUIStack>
      </WizardPanel>

      <WizardFooter
        wideMode={wideMode}
        handleClickContinue={handleSave}
        handleClickBack={() => handlePrevNext(-1)}
        continueButtonText="Save"
        continueButtonDisabled={isLoading || isSaving || mappedFormData.mappedFormId == null}
      />
    </>
  )
}
